import RechargeCooldownLabel from "../../RechargeCooldownLabel";
import { RechargeCooldownError } from "@/store/rechargeCooldown";
import React, { useEffect, useRef, useState } from "react";
import "../flatRecharge.less";
import { useTranslationPlus } from "@/Core/i18n/useTranslationPlus";
import {
  useChangeFlatRecharge,
  useWalletAll,
  useWalletRechargeFlat,
} from "@/store/walletStore";
import { useUserInfo } from "@/store/userInfo";
import FormUI from '@/componentsUI/Form';
import FromItemPlus from "@/components/FormItemPlus/FromItemPlus";
import LoadingUI from "@/componentsUI/LoadingUI/LoadingUI";
import {
  IFWalletRechargeFlatReq,
} from "@/api/apiReq/IFWalletReq";
import FlatPix from "../pix/FlatPix";
import ButtonUI from "@/componentsUI/Button";
import { AmountMultiplierConversion } from "@/Core/utils/currencyDealCenter";
import useDialogManager from "@/utils/useDialogManager";
import DepositCurrency from "../compoments/depositCurrency/DepositCurrency";
import DepositWay from "../compoments/depositWay/DepositWay";
import MobileInput from "../compoments/mobileInput/MobileInput";
import { getUserMobile } from "@/utils/mobileUtils";
import DepositWarningDialog from "../compoments/depositWarningDialog/DepositWarningDialog";
import { useRechargeCurrency } from "../../useRechargeCurrency";
import { useRechargePaymentMethod } from "../useRechargePaymentMethod";
import { feedback } from "@/utils/feedback";

const FlatRechargeWarning = () => {
  const { t } = useTranslationPlus();
  const { data: wallets } = useWalletAll();
  const { data: userInfo } = useUserInfo();
  const [form] = FormUI.useForm();
  const { params, displayCurrencyType, changeRecharge } = useRechargeCurrency(wallets?.flat, form);
  const { isValidating, data, error, mutate } = useChangeFlatRecharge(params);
  const { trigger, isMutating, isRechargeBlocked } = useWalletRechargeFlat();
  const { openDialogIFrame } = useDialogManager();
  const formValidator = useRef(null);
  const { rule, changeType } = useRechargePaymentMethod(data?.payParams, form, params?.currencyType);
  const [showWarningDialog, setShowWarningDialog] = useState(false);

  useEffect(() => {
    setShowWarningDialog(false);
    formValidator.current = false;
  }, [rule, params?.currencyType]);

  function recharge() {
    if (!rule || error || isValidating || isMutating || isRechargeBlocked) return;
    formValidator.current = true;
    form
      .validate()
      .then(() => {
        setShowWarningDialog(true);
      })
      .catch(() => {
        formValidator.current = false;
      });
  }

  function handleConfirmRecharge() {
    if (!rule || error || isValidating || isMutating || isRechargeBlocked) return;
    setShowWarningDialog(false);
    const formValues = form.getFieldsValue();
    const selectedWallet = wallets?.flat?.find(
      (wallet) => wallet.currencyType === formValues?.walletType
    );

    const paymentData: IFWalletRechargeFlatReq = {
      currencyType:
        selectedWallet?.serverCurrencyType || formValues?.walletType,
      money: AmountMultiplierConversion(formValues?.amount, {
        currencyType: selectedWallet?.currencyType || formValues?.walletType,
        serverCurrencyType: selectedWallet?.serverCurrencyType,
      }),
      payCode: formValues?.payType?.data?.payCode,
      mobile: formValues?.mobile,
      rechargeMobileUserId: userInfo?.userId,
    };

    trigger(paymentData)
      .then((res) => {
        if (res?.payUrl) {
          openDialogIFrame({
            title: t("Wallet.Wallet_PayUrlTitle"),
            url: res?.payUrl,
            type: "pay",
          });
        } else {
          feedback.success(t("Wallet.Payment request submitted successfully"));
        }
      })
      .catch((e) => {
        if (e instanceof RechargeCooldownError) return;
        feedback.error(t(e?.code));
      })
      .finally(() => {
        formValidator.current = false;
      });
  }

  function handleCloseWarningDialog() {
    setShowWarningDialog(false);
    formValidator.current = false;
  }

  useEffect(() => {
    const mobile = getUserMobile(userInfo?.mobile, userInfo?.userId);
    if (mobile) {
      form.setFieldValue("mobile", mobile);
    }
  }, [userInfo]);

  useEffect(() => {
    if (error) {
      console.error("Flat recharge error:", {
        error,
        errorCode: error?.code,
        errorMessage: error?.message,
        params,
      });
    }
  }, [error, params]);

  return (
    <div className="flat-Recharge">
      <FormUI form={form} layout={"vertical"} autoComplete={"off"}>
        <LoadingUI
          isLoading={isValidating}
          error={error}
          dataList={data}
          retryFunc={mutate}
        >
          <div className="deposit-row">
            <FromItemPlus
              field={"walletType"}
              label={t("Wallet.Currency")}
              className="deposit-currency-item"
            >
              <DepositCurrency
                currencys={wallets?.flat}
                onChange={(_value: any, option: any) => {
                  changeRecharge(option?._index);
                }}
              />
            </FromItemPlus>
          </div>
          <div className="deposit-method-section">
            <FromItemPlus
              field={"payType"}
              label={t("Wallet.Method")}
              className="deposit-way-item"
              shouldUpdate
            >
              {(values) => (
                <DepositWay
                  payParams={data?.payParams}
                  onChange={changeType}
                  value={values?.payType}
                />
              )}
            </FromItemPlus>
          </div>
          <div className="deposit-mobile-section">
            <FromItemPlus
              field={"mobile"}
              label={t("Wallet.Mobile Number")}
              className="deposit-mobile-item"
              rules={[
                {
                  required: true,
                  message: t("Wallet.Please enter mobile number"),
                },
                {
                  validator: (value, cb) => {
                    if (value && (value.length < 8 || value.length > 10)) {
                      return cb(t("Wallet.Mobile number must be"));
                    }
                    return cb();
                  },
                },
              ]}
            >
              <MobileInput />
            </FromItemPlus>
            <div className="important-notice">
              {t("Wallet.DepositPhoneNoticeShort")}
            </div>
          </div>
          <FlatPix
            data={data}
            form={form}
            currencyType={displayCurrencyType}
            serverCurrencyType={params?.currencyType}
            formValidator={formValidator}
            rule={rule}
          />
        </LoadingUI>
      </FormUI>
      <ButtonUI
        className={`recharge-btn`}
        data-recharge-spin-entry-anchor="deposit-submit"
        type="primary"
        loading={isMutating}
        disabled={!rule || !!error || isValidating || isMutating || isRechargeBlocked}
        onClick={recharge}
      >
        <RechargeCooldownLabel>{t("Wallet.Deposit")}</RechargeCooldownLabel>
      </ButtonUI>
      <DepositWarningDialog
        visible={showWarningDialog}
        onClose={handleCloseWarningDialog}
        onConfirm={handleConfirmRecharge}
      />
    </div>
  );
};

export default FlatRechargeWarning;
